"use client";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
} from "@tiendanube/nube-sdk-react-runtime";
import type { CSSProperties, ReactElement } from "react";
import { classNames } from "../../helpers/class-names";
import { useNubeSDK } from "../../hooks/use-nube-sdk";
import { Icon } from "../Icon";
import { useToastPosition } from "./useToastPosition";

const TOAST_DURATION = 3000;
// Matches the exit transition of `.toast-leaving`
const TOAST_EXIT_DURATION = 300;

export type ToastRootProps = {
	variant?: "success" | "error" | "warning" | "info";
	duration?: number;
	children: ReactElement | ReactElement[];
	style?: CSSProperties;
	onClose?: unknown;
	__internalId?: string;
};

export const ToastRoot = ({
	variant = "success",
	duration = TOAST_DURATION,
	__internalId,
	children,
	style,
	onClose,
}: ToastRootProps) => {
	const { send } = useNubeSDK("adapter");
	const [isLeaving, setIsLeaving] = useState(false);
	const [isClosed, setIsClosed] = useState(false);
	const toastRef = useRef<HTMLDivElement>(null);
	const currentPosition = useToastPosition(toastRef);
	// Kept in a ref so re-renders (e.g. a new `onClose` reference from the
	// worker) don't restart the exit timer, keeping the close event single-shot.
	const notifyCloseRef = useRef<() => void>();
	notifyCloseRef.current = () => {
		if (!onClose || !__internalId) return;
		send(`custom:toastRoot:${__internalId}:close`, () => ({
			ui: {
				values: {
					[__internalId]: undefined,
				},
			},
		}));
	};

	const startCloseSequence = useCallback(() => {
		setIsLeaving(true);
	}, []);

	useEffect(() => {
		if (duration > 0) {
			const timer = setTimeout(() => {
				startCloseSequence();
			}, duration);

			return () => clearTimeout(timer);
		}
		return undefined;
	}, [duration, startCloseSequence]);

	useEffect(() => {
		if (!isLeaving) return undefined;

		const timer = setTimeout(() => {
			setIsClosed(true);
			notifyCloseRef.current?.();
		}, TOAST_EXIT_DURATION);

		return () => clearTimeout(timer);
	}, [isLeaving]);

	if (isClosed) return null;

	return (
		<div
			id={__internalId}
			className={classNames(
				"nube-toast-root toast-container",
				currentPosition && `toast-${currentPosition}`,
			)}
			ref={toastRef}
		>
			<div
				className={classNames("toast", `toast-${variant}`, {
					"toast-leaving": isLeaving,
				})}
				style={style}
			>
				<div className="toast-content">{children}</div>
				<button
					type="button"
					className="toast-close"
					onClick={startCloseSequence}
					// TODO: internacionalizar
					aria-label="close"
				>
					<Icon name="close" size={16} />
				</button>
			</div>
		</div>
	);
};
